import {
  deriveWorklistStatus,
  jobCategoryTree,
  statusesInTab,
  tabLabel,
  worklistStatus,
  type JobTabKey,
  type WorklistStatusKey,
} from '@fieldops/shared'
import { useMemo } from 'react'
import { cn } from '../../lib/cn.js'
import { JOB_STAGES, stageLabel, type JobStageKey } from '../../lib/job-stages.js'
import type { RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import type { ScheduleView } from '../repeat/drafts.js'
import { dayLabel, everyLabel } from '../repeat/rule.js'
import { Select } from '../ui.js'
import { Label, Panel } from './panes.js'
import type { Job } from './types.js'

export type JobCategoryOption = RouterOutputs['jobs']['categories'][number]

/**
 * What state the job is in and what kind of work it is.
 *
 * These two facts used to be chips in the card's header, wedged between a
 * client name that can run to sixty characters and four action buttons, which
 * left the status control competing for the same row as the close button. They
 * are the job's own record rather than furniture, so they read as the first
 * panel on Work — above the site and the client, which are the *other* records
 * a job points at. Priority stays on the job and on the board; it is not
 * drawn here.
 *
 * There is one status control on the screen and it is this one: the row of
 * stage tiles. A second copy in the header would be two controls writing the
 * same column.
 *
 * "Kind of work" is the same wording and the same two-level `optgroup` list as
 * the New job form, because it is the same question asked later. It falls back
 * to `Job.title` and then to "Not categorised" for a job with no category; the
 * title is never the heading, per decision 63.
 */
export function JobDetailsCard({
  job,
  stage,
  canEdit,
  categories,
  isSaving,
  onChangeStage,
  onChangeCategory,
  repeat = null,
  onOpenSchedule,
}: {
  job: Job
  stage: JobStageKey
  /** The job's repeat schedule, when it is on one. Decision 96. */
  repeat?: ScheduleView | null
  onOpenSchedule?: () => void
  /** Tiles that pick for anyone with `JOB_EDIT`, the same tiles read-only for everyone else. */
  canEdit: boolean
  /** Empty until the list arrives, which is why the current one is added below. */
  categories: readonly JobCategoryOption[]
  isSaving: boolean
  onChangeStage: (stage: JobStageKey) => void
  onChangeCategory: (categoryId: string | null) => void
}) {
  return (
    <Panel className="gap-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Label>Job details</Label>

        <div className="flex min-w-0 items-center gap-3">
          <span className="shrink-0 text-[13px] whitespace-nowrap text-ink-500">Kind of work</span>
          {canEdit ? (
            <div className="flex min-w-0 flex-col gap-1">
              <CategoryPicker
                job={job}
                categories={categories}
                isSaving={isSaving}
                onChange={onChangeCategory}
              />
              {/* The one job in 74,300 that carries a title, and the handful
                  typed since. It is what described the job before there was a
                  category on it, so it stays visible until there is one. */}
              {!job.category && job.title ? (
                <span className="truncate text-[12.5px] text-ink-500">{job.title}</span>
              ) : null}
            </div>
          ) : (
            <span className="min-w-0 text-[13px] text-ink-900">
              <CategoryLabel job={job} />
            </span>
          )}
        </div>
      </div>

      <StageTrack
        job={job}
        stage={stage}
        canEdit={canEdit}
        isSaving={isSaving}
        onChange={onChangeStage}
      />

      {repeat ? <RepeatLine schedule={repeat} onOpen={onOpenSchedule} /> : null}
    </Panel>
  )
}

/** "Repeats every 6 months · next 18 Mar 2027", a way through to the Schedule tab. */
function RepeatLine({ schedule, onOpen }: { schedule: ScheduleView; onOpen?: () => void }) {
  const next = schedule.slots.find((slot) => slot.isNext)
  const text = schedule.stoppedAt
    ? 'Repeat schedule stopped'
    : `Repeats ${everyLabel(schedule)}${next ? ` · next ${dayLabel(next.dueOn)}` : ''}`

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-2 self-start rounded-md text-[13px] font-medium text-ink-700 hover:text-ink-900"
    >
      <Icon name="repeat" className="size-4 text-ink-500" />
      {text}
    </button>
  )
}

/** The Jobs list tab each pickable stage is read through. Unsuccessful has none. */
const STAGE_TAB: Partial<Record<JobStageKey, JobTabKey>> = {
  QUOTE: 'QUOTE',
  WORK_ORDER: 'WORK_ORDER',
  COMPLETE: 'COMPLETED',
}

/**
 * The stages as tiles, and where the job is inside its own.
 *
 * The stage is picked; the steps inside it are read. The current tile's
 * segments are the Jobs list's statuses for that tab — Draft / Priced / Sent,
 * Unscheduled / Booked / Follow-up, Invoice pending / Awaiting payment / Paid —
 * derived by `deriveWorklistStatus` from the job's own dates, so the card and
 * the queue cannot disagree and nothing here is a second way to write them.
 * The current tile is outlined in Buster Red, twice the width of the others so
 * its step names fit, and names its steps; the stages already behind the job
 * read Done on a navy bar, and the ones still ahead are a grey bar. Decisions
 * 35 and 77, ADR 025.
 *
 * Unsuccessful is still one of the four picks, but it is not a tile until the
 * job is unsuccessful: a fourth tile in a column this narrow truncates "Work
 * Order" and the step names. Until then it is the quiet radio under the row.
 * It has no steps — it says everything there is to say.
 *
 * The track is its own container, so it stacks by the width it is given (the
 * Work column beside the feed, the phone sheet) rather than by the viewport.
 */
function StageTrack({
  job,
  stage,
  canEdit,
  isSaving,
  onChange,
}: {
  job: Job
  stage: JobStageKey
  canEdit: boolean
  isSaving: boolean
  onChange: (stage: JobStageKey) => void
}) {
  const current = deriveWorklistStatus(job)
  const order = JOB_STAGES.findIndex((entry) => entry.key === stage)
  const lost = stage === 'UNSUCCESSFUL'
  const tiles = JOB_STAGES.filter((entry) => entry.key !== 'UNSUCCESSFUL' || lost)

  return (
    <div
      role={canEdit ? 'radiogroup' : 'group'}
      aria-label="Job status"
      className="@container flex flex-col gap-1"
    >
      <div className="flex flex-wrap gap-2 @[30rem]:flex-nowrap">
        {tiles.map((entry, index) => {
          const tab = STAGE_TAB[entry.key]
          const steps = tab ? statusesInTab(tab).filter((key) => key !== 'CLOSED_LOST') : []

          return (
            <StageTile
              key={entry.key}
              label={tab ? tabLabel(tab) : stageLabel(entry.key)}
              steps={steps}
              active={entry.key === stage}
              current={current}
              behind={!lost && index < order}
              pickable={canEdit}
              disabled={isSaving}
              onPick={() => onChange(entry.key)}
            />
          )
        })}
      </div>

      {canEdit && !lost ? (
        <button
          type="button"
          role="radio"
          aria-checked={false}
          aria-label={stageLabel('UNSUCCESSFUL')}
          disabled={isSaving}
          onClick={() => onChange('UNSUCCESSFUL')}
          className="self-end rounded px-1 text-[12.5px] text-ink-500 transition-colors hover:text-accent-600 disabled:cursor-wait"
        >
          Mark unsuccessful
        </button>
      ) : null}
    </div>
  )
}

function StageTile({
  label,
  steps,
  active,
  current,
  behind,
  pickable,
  disabled,
  onPick,
}: {
  label: string
  steps: readonly WorklistStatusKey[]
  active: boolean
  current: WorklistStatusKey
  /** A stage the job has already been through. */
  behind: boolean
  pickable: boolean
  disabled: boolean
  onPick: () => void
}) {
  const at = active ? steps.indexOf(current) : -1
  const here = at >= 0 ? worklistStatus(current) : null
  const split = active && steps.length > 0

  const body = (
    <>
      <span className="flex w-full min-w-0 items-baseline justify-between gap-2">
        <span
          className={cn(
            'truncate text-[11px] font-bold tracking-[0.1em] uppercase',
            active ? 'text-accent-600' : 'text-ink-500',
          )}
        >
          {label}
        </span>
        {here ? (
          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-ink-500">
            {at + 1} of {steps.length}
          </span>
        ) : null}
      </span>

      {here ? (
        <span title={here.meaning} className="truncate text-[13.5px] font-bold text-brand-800">
          {here.label}
        </span>
      ) : behind ? (
        <span className="truncate text-[13.5px] font-semibold text-brand-800">Done</span>
      ) : null}

      {split ? (
        <span className="mt-0.5 grid w-full grid-flow-col auto-cols-fr gap-1">
          {steps.map((key, index) => (
            <span key={key} className="flex min-w-0 flex-col gap-1">
              <span
                aria-hidden="true"
                className={cn(
                  'h-[5px] rounded-full',
                  index <= at ? 'bg-accent-500' : 'bg-accent-200',
                )}
              />
              <span
                title={worklistStatus(key).meaning}
                className={cn(
                  'truncate text-[11px]',
                  index === at ? 'font-bold text-accent-600' : 'text-ink-500',
                )}
              >
                {worklistStatus(key).label}
              </span>
            </span>
          ))}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            'mt-auto h-[5px] w-full rounded-full',
            active ? 'bg-accent-500' : behind ? 'bg-brand-800' : 'bg-ink-200',
          )}
        />
      )}
    </>
  )

  const className = cn(
    'flex min-w-0 flex-1 basis-0 flex-col items-start gap-1 rounded-[10px] px-3 py-2 text-left transition-colors @[30rem]:min-w-[7.5rem]',
    active ? 'bg-accent-50 ring-2 ring-accent-500 ring-inset' : 'bg-ink-100',
    split && 'order-first basis-full @[30rem]:order-none @[30rem]:flex-[2_1_0%]',
  )

  if (!pickable) {
    return (
      <div className={className} aria-current={active ? 'step' : undefined}>
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      disabled={disabled}
      onClick={active ? undefined : onPick}
      className={cn(
        className,
        !active && 'hover:bg-ink-200/70',
        'disabled:cursor-wait',
      )}
    >
      {body}
    </button>
  )
}

/**
 * The kind of work, changeable in place.
 *
 * **The job's own category is always an option, even when the list does not
 * hold it.** Two things put it there. Retiring a category takes it off every
 * picker and leaves it on the jobs that carry it (decision 71), so the several
 * thousand jobs wearing a retired one would otherwise open showing "Not
 * categorised" — and a select whose value has no option reads as blank, so the
 * next save would write the blank back. The list also arrives a moment after
 * the job does, and the same rule covers that with no loading state and no
 * control that changes shape under the pointer.
 */
function CategoryPicker({
  job,
  categories,
  isSaving,
  onChange,
}: {
  job: Job
  categories: readonly JobCategoryOption[]
  isSaving: boolean
  onChange: (categoryId: string | null) => void
}) {
  const sections = useMemo(() => jobCategoryTree(categories), [categories])
  const current = job.category
  const listed = current ? categories.some((row) => row.id === current.id) : true

  return (
    <div className="flex items-center gap-2">
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: current?.colour ?? 'var(--color-ink-300)' }}
      />
      <Select
        aria-label="Kind of work"
        value={current?.id ?? ''}
        disabled={isSaving}
        onChange={(event) => onChange(event.target.value || null)}
        className="w-[300px] max-w-full min-w-0 text-[14px]"
      >
        <option value="">Not categorised</option>

        {current && !listed ? (
          <option value={current.id}>{`${current.name} (retired)`}</option>
        ) : null}

        {sections.map((section, index) =>
          section.group ? (
            <optgroup key={section.group.id} label={section.group.name}>
              {section.options.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </optgroup>
          ) : (
            section.options.map((category) => (
              <option key={`${index}-${category.id}`} value={category.id}>
                {category.name}
              </option>
            ))
          ),
        )}
      </Select>
    </div>
  )
}

function CategoryLabel({ job }: { job: Job }) {
  if (job.category) {
    return (
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{
            backgroundColor: job.category.colour ?? 'var(--color-ink-400)',
          }}
        />
        <span className="truncate">{job.category.name}</span>
      </span>
    )
  }

  if (job.title) return <span className="block truncate">{job.title}</span>

  return <span className="text-ink-400">Not categorised</span>
}
